import axios from 'axios';
import { omitBy } from 'lodash';
import { unset } from 'lodash';
import { ThunkAction } from 'redux-thunk';
import { CommonRootState } from '../combineReducers';
import {
  IContactInformationStatus,
  IHiringClientsStatus,
  IOperatingDetailsStatus,
  IOrganizationProfileStatus
} from '../interfaces/organizationProfileNotifications';

export const updateOrganizationProfileNotifications = (
  organizationId: string
): ThunkAction<Promise<IOrganizationProfileStatus>, CommonRootState, null, Actions> => dispatch => {
  return new Promise<IOrganizationProfileStatus>(async (resolve, reject) => {
    try {
      const excludeKeys = ['contactInformationSection.branchLocationsIsCompleted'];
      interface IOrganizationWithRegistrationNumbers {
        organizationRegistrationNumbers?: Array<{ typeId: number; value: string | null }>;
      }
      const [pageStatusResponse, organizationResponse] = await Promise.all([
        axios.get<IOrganizationProfileStatus>(`/api/v3.1/organizations/${organizationId}/pageStatus`),
        axios
          .get<IOrganizationWithRegistrationNumbers>(`/api/v3.3/organizations/${organizationId}`)
          .catch(() => ({ data: {} as IOrganizationWithRegistrationNumbers }))
      ]);
      const data = pageStatusResponse.data;
      excludeKeys.forEach(path => {
        unset(data, path);
      });

      const CREDIT_SAFE_TYPE_ID = 6;
      const registrationNumbers = organizationResponse.data.organizationRegistrationNumbers || [];
      const creditSafeEntry = registrationNumbers.find(n => n.typeId === CREDIT_SAFE_TYPE_ID);
      if (data.operationDetailsSection) {
        data.operationDetailsSection.creditSafeNumberIsCompleted = Boolean(creditSafeEntry?.value);
      }

      dispatch(changeOrganizationProfileNotifications(data));
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const updateOrganizationProfileContactInformationNotifications = (
  organizationId: string
): ThunkAction<Promise<IContactInformationStatus>, CommonRootState, null, Actions> => dispatch => {
  return new Promise<IContactInformationStatus>(async (resolve, reject) => {
    try {
      const excludeKeys = ['branchLocationsIsCompleted'];
      const { data } = await axios.get<IContactInformationStatus>(
        `/api/v3.1/organizations/${organizationId}/pageStatus/contactInformation`
      );
      excludeKeys.forEach(path => {
        unset(data, path);
      });
      dispatch(
        changeOrganizationProfileNotifications({
          contactInformationSection: data
        })
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

export const updateOrganizationProfileOperationDetailsNotifications = (
  organizationId: string,
  extraFields?: Partial<IOperatingDetailsStatus>
): ThunkAction<Promise<IOperatingDetailsStatus>, CommonRootState, null, Actions> => dispatch => {
  return new Promise<IOperatingDetailsStatus>(async (resolve, reject) => {
    try {
      const { data } = await axios.get<IOperatingDetailsStatus>(
        `/api/v3.1/organizations/${organizationId}/pageStatus/operationDetails`
      );
      const nonNullData = omitBy(data, value => value === null) as Partial<IOperatingDetailsStatus>;
      if (extraFields) Object.assign(nonNullData, extraFields);
      dispatch(
        changeOrganizationProfileNotifications({
          operationDetailsSection: nonNullData
        })
      );
      resolve({ ...data, ...extraFields });
    } catch (error) {
      reject(error);
    }
  });
};

export const updateOrganizationProfileHiringClientsNotifications = (
  organizationId: string
): ThunkAction<Promise<IHiringClientsStatus>, CommonRootState, null, Actions> => dispatch => {
  return new Promise<IHiringClientsStatus>(async (resolve, reject) => {
    try {
      const { data } = await axios.get<IHiringClientsStatus>(
        `/api/v3.1/organizations/${organizationId}/pageStatus/hiringClients`
      );
      dispatch(
        changeOrganizationProfileNotifications({
          hiringClientSection: data
        })
      );
      resolve(data);
    } catch (error) {
      reject(error);
    }
  });
};

type DeepPartialOrganizationProfileStatus = {
  contactInformationSection?: Partial<IContactInformationStatus>;
  operationDetailsSection?: Partial<IOperatingDetailsStatus>;
  hiringClientSection?: Partial<IHiringClientsStatus>;
};

export const changeOrganizationProfileNotifications = (payload: DeepPartialOrganizationProfileStatus) =>
  ({
    type: 'CHANGE_ORGANIZATION_PROFILE_NOTIFICATIONS',
    payload
  } as const);

export type Actions = ReturnType<typeof changeOrganizationProfileNotifications>;
